import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useTracks,
  useParticipants,
  useLocalParticipant,
  useConnectionState,
} from '@livekit/components-react';
import { Track, ConnectionState } from 'livekit-client';
import '@livekit/components-styles';
import { Video, Mic, MicOff, VideoOff, PhoneOff, Minimize2, Users, RefreshCw, Monitor, Move } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Context for LiveKit state
const LiveKitContext = createContext(null);

export const useLiveKitContext = () => useContext(LiveKitContext);

/**
 * Simple LiveKit Video Widget - Production-grade video
 */
export const LiveKitVideoWidget = ({ roomCode, username, isHost = false, onClose, minimizable = true, fullscreenScreenShare = false }) => {
  const [token, setToken] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [minimized, setMinimized] = useState(false);
  
  // Draggable state - must be declared before any returns
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  const connect = useCallback(async () => {
    if (!roomCode) return;
    
    setIsConnecting(true);
    setError(null);
    
    try {
      const participantName = username || (isHost ? `Host_${Date.now()}` : `Client_${Date.now()}`);
      
      const response = await fetch(`${API}/api/livekit/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          room: roomCode.toUpperCase(), 
          username: participantName, 
          is_host: isHost 
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get video token');
      }

      const data = await response.json();
      setToken(data.token);
      setServerUrl(data.url);
      
    } catch (err) {
      console.error('LiveKit connection error:', err);
      setError(err.message);
      toast.error('Failed to connect video: ' + err.message);
    }
    
    setIsConnecting(false);
  }, [roomCode, username, isHost]);

  // Auto-connect on mount
  useEffect(() => {
    if (roomCode && !token) {
      connect();
    }
  }, [roomCode, token, connect]);

  // Handle drag move - must be before any returns
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      
      const newX = Math.max(0, Math.min(window.innerWidth - 420, dragStartRef.current.posX + deltaX));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, dragStartRef.current.posY + deltaY));
      
      setPosition({ x: newX, y: newY });
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);

  const disconnect = () => {
    setToken(null);
    setServerUrl(null);
    if (onClose) onClose();
  };

  // Handle drag start
  const handleDragStart = (e) => {
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('video')) return;
    
    setIsDragging(true);
    const rect = dragRef.current?.getBoundingClientRect();
    if (rect) {
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        posX: rect.left,
        posY: rect.top
      };
    }
  };

  // Reset position to default
  const resetPosition = () => setPosition({ x: null, y: null });

  const positionStyle = position.x !== null 
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : { right: 16, bottom: 16 };

  // Minimized view - keeps position
  if (minimized && minimizable) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed z-[9999] p-3 bg-green-600 hover:bg-green-500 rounded-full shadow-xl flex items-center gap-2"
        style={positionStyle}
      >
        <Video className="w-5 h-5 text-white" />
        <span className="text-white font-bold text-sm">{roomCode}</span>
      </button>
    );
  }

  // Not connected yet - show connect button
  if (!token || !serverUrl) {
    return (
      <div className="fixed z-[9999] w-80 bg-gray-900 rounded-xl shadow-2xl border border-gray-700 overflow-hidden" style={positionStyle}>
        <div className="bg-gray-800 px-4 py-3 flex items-center justify-between">
          <span className="text-white font-medium">Video Call</span>
          <div className="flex items-center gap-2">
            {minimizable && (
              <button onClick={() => setMinimized(true)} className="text-gray-400 hover:text-white">
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
            {onClose && (
              <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">×</button>
            )}
          </div>
        </div>
        
        <div className="p-6 text-center">
          {error ? (
            <>
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={connect}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-5 h-5" />
                Retry
              </button>
            </>
          ) : isConnecting ? (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <div className="w-12 h-12 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
              </div>
              <p className="text-green-400 font-medium">Connecting...</p>
              <p className="text-gray-500 text-xs mt-1">Room: {roomCode}</p>
              <p className="text-gray-600 text-xs mt-2">Getting video token...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-400 mb-4">Room: {roomCode}</p>
              <button
                onClick={connect}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl"
              >
                Join Video Call
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Connected - show LiveKit room
  return (
    <div 
      ref={dragRef}
      className={`fixed z-[9999] w-[420px] bg-gray-900 rounded-xl shadow-2xl border border-gray-700 overflow-hidden ${isDragging ? 'cursor-grabbing' : ''}`}
      style={positionStyle}
    >
      {/* Drag Handle */}
      <div 
        onMouseDown={handleDragStart}
        className="bg-gray-800 px-3 py-1 flex items-center justify-between cursor-grab border-b border-gray-700"
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500 hover:bg-red-400" onClick={disconnect} title="Close" />
            <div className="w-2 h-2 rounded-full bg-yellow-500 hover:bg-yellow-400" onClick={() => setMinimized(true)} title="Minimize" />
            <div className="w-2 h-2 rounded-full bg-green-500 hover:bg-green-400" onClick={resetPosition} title="Reset Position" />
          </div>
          <span className="text-[10px] text-gray-500">Drag to move</span>
        </div>
        <span className="text-[10px] text-gray-600">{roomCode}</span>
      </div>
      
      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        connect={true}
        video={false}  // Start without video for faster connection
        audio={true}
        onConnected={() => {
          toast.success('Connected! Enabling video...');
        }}
        onDisconnected={() => {
          toast.info('Video disconnected');
        }}
        onError={(err) => {
          console.error('LiveKit error:', err);
          toast.error('Video error: ' + err.message);
        }}
        options={{
          adaptiveStream: true,
          dynacast: true,
          disconnectOnPageLeave: true,
          publishDefaults: {
            simulcast: false,
            videoCodec: 'vp8',
          },
          websocketTimeout: 10000, // 10 second timeout
          stopLocalTrackOnUnpublish: true,
        }}
        connectOptions={{
          autoSubscribe: true,
        }}
      >
        <RoomContent 
          roomCode={roomCode} 
          onDisconnect={disconnect}
          onMinimize={() => setMinimized(true)}
          minimizable={minimizable}
          fullscreenScreenShare={fullscreenScreenShare}
        />
      </LiveKitRoom>
    </div>
  );
};

// Inner component that has access to LiveKit hooks
const RoomContent = ({ roomCode, onDisconnect, onMinimize, minimizable, fullscreenScreenShare = false }) => {
  const connectionState = useConnectionState();
  const participants = useParticipants();
  const localParticipant = useLocalParticipant();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone, Track.Source.ScreenShare]);
  
  const [audioEnabled, setAudioEnabled] = useState(false); // Start muted
  const [videoEnabled, setVideoEnabled] = useState(false); // Start without video
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Initializing...');

  // Track connection status changes
  useEffect(() => {
    switch (connectionState) {
      case ConnectionState.Connecting:
        setConnectionStatus('Connecting to server...');
        break;
      case ConnectionState.Connected:
        setConnectionStatus('Connected!');
        break;
      case ConnectionState.Reconnecting:
        setConnectionStatus('Reconnecting...');
        break;
      case ConnectionState.Disconnected:
        setConnectionStatus('Disconnected');
        break;
      default:
        setConnectionStatus('Starting...');
    }
  }, [connectionState]);

  // Auto-enable camera after connection is established
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && !cameraStarted && localParticipant.localParticipant) {
      setCameraStarted(true);
      setConnectionStatus('Starting camera...');
      // Small delay to let audio stabilize first
      setTimeout(async () => {
        try {
          await localParticipant.localParticipant.setCameraEnabled(true);
          setVideoEnabled(true);
          setConnectionStatus('Ready');
          toast.success('Video connected!');
        } catch (err) {
          console.error('Camera enable error:', err);
          setConnectionStatus('Camera error');
          toast.error('Could not start camera');
        }
      }, 300);
    }
  }, [connectionState, cameraStarted, localParticipant]);

  // Get local tracks
  const localVideoTrack = tracks.find(
    t => t.participant.isLocal && t.source === Track.Source.Camera
  );
  const localAudioTrack = tracks.find(
    t => t.participant.isLocal && t.source === Track.Source.Microphone
  );
  const localScreenTrack = tracks.find(
    t => t.participant.isLocal && t.source === Track.Source.ScreenShare
  );

  // Get remote tracks
  const remoteVideoTrack = tracks.find(
    t => !t.participant.isLocal && t.source === Track.Source.Camera
  );
  const remoteScreenTrack = tracks.find(
    t => !t.participant.isLocal && t.source === Track.Source.ScreenShare
  );

  const toggleAudio = async () => {
    if (localParticipant.localParticipant) {
      await localParticipant.localParticipant.setMicrophoneEnabled(!audioEnabled);
      setAudioEnabled(!audioEnabled);
    }
  };

  const toggleVideo = async () => {
    if (localParticipant.localParticipant) {
      await localParticipant.localParticipant.setCameraEnabled(!videoEnabled);
      setVideoEnabled(!videoEnabled);
    }
  };

  const toggleScreenShare = async () => {
    if (localParticipant.localParticipant) {
      try {
        if (screenShareEnabled) {
          await localParticipant.localParticipant.setScreenShareEnabled(false);
          setScreenShareEnabled(false);
          toast.info('Screen share stopped');
        } else {
          await localParticipant.localParticipant.setScreenShareEnabled(true, {
            audio: true, // Include system audio if available
            video: {
              displaySurface: 'monitor', // Prefer full screen
            }
          });
          setScreenShareEnabled(true);
          toast.success('Sharing your screen - Client can see your DAW!');
        }
      } catch (err) {
        console.error('Screen share error:', err);
        if (err.name === 'NotAllowedError') {
          toast.error('Screen share was cancelled');
        } else {
          toast.error('Could not start screen share');
        }
      }
    }
  };

  const isConnected = connectionState === ConnectionState.Connected;
  const remoteParticipants = participants.filter(p => !p.isLocal);

  // If client with fullscreen screen share and remote is sharing, show fullscreen view
  if (fullscreenScreenShare && remoteScreenTrack) {
    return (
      <>
        {/* Fullscreen Screen Share for Client */}
        <div className="fixed inset-0 z-[9998] bg-black">
          <video
            ref={el => {
              if (el && remoteScreenTrack.publication.track) {
                remoteScreenTrack.publication.track.attach(el);
              }
            }}
            autoPlay
            playsInline
            className="w-full h-full object-contain"
          />
          <div className="absolute top-4 left-4 bg-purple-600/90 px-3 py-1.5 rounded-lg text-sm text-white font-medium flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Host is sharing screen
          </div>
        </div>
        
        {/* Small floating video controls */}
        <div className="fixed bottom-4 right-4 z-[9999] w-80 bg-gray-900 rounded-xl shadow-2xl border border-gray-700 overflow-hidden">
          <div className="bg-gray-800 px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm text-gray-300">{remoteParticipants.length + 1} in room</span>
            </div>
          </div>
          
          {/* Camera Views Row */}
          <div className="flex gap-1 p-1 h-20">
            {remoteVideoTrack && (
              <div className="flex-1 relative rounded-lg overflow-hidden bg-gray-900">
                <video
                  ref={el => {
                    if (el && remoteVideoTrack.publication.track) {
                      remoteVideoTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-[9px] text-green-400">HOST</div>
              </div>
            )}
            {localVideoTrack && videoEnabled && (
              <div className="w-20 relative rounded-lg overflow-hidden border border-blue-500 bg-gray-900">
                <video
                  ref={el => {
                    if (el && localVideoTrack.publication.track) {
                      localVideoTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
                <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-[9px] text-blue-400">YOU</div>
              </div>
            )}
          </div>
          
          {/* Controls */}
          <div className="bg-gray-800 px-3 py-2 flex items-center justify-center gap-2">
            <button
              onClick={toggleAudio}
              className={`p-2 rounded-full ${audioEnabled ? 'bg-gray-700' : 'bg-red-600'}`}
            >
              {audioEnabled ? <Mic className="w-4 h-4 text-white" /> : <MicOff className="w-4 h-4 text-white" />}
            </button>
            <button
              onClick={toggleVideo}
              className={`p-2 rounded-full ${videoEnabled ? 'bg-gray-700' : 'bg-red-600'}`}
            >
              {videoEnabled ? <Video className="w-4 h-4 text-white" /> : <VideoOff className="w-4 h-4 text-white" />}
            </button>
            <button
              onClick={onDisconnect}
              className="p-2 rounded-full bg-red-600"
            >
              <PhoneOff className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
        
        <RoomAudioRenderer />
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-gray-800 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isConnected ? (
            <>
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm text-gray-300">{remoteParticipants.length + 1} in room</span>
            </>
          ) : (
            <>
              <div className="w-3 h-3 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin" />
              <span className="text-sm text-yellow-400">{connectionStatus}</span>
            </>
          )}
          <span className="text-xs text-gray-500">| {roomCode}</span>
        </div>
        <div className="flex items-center gap-1">
          {minimizable && (
            <button onClick={onMinimize} className="p-1 hover:bg-gray-700 rounded">
              <Minimize2 className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {/* Video Area - Split View */}
      <div className="bg-black" style={{ height: screenShareEnabled || remoteScreenTrack ? '320px' : '280px' }}>
        <div className="h-full flex flex-col gap-1 p-1">
          {/* Screen Share Area (if active) */}
          {(screenShareEnabled || remoteScreenTrack) && (
            <div className="flex-1 relative rounded-lg overflow-hidden bg-gray-900 border border-purple-500/50">
              {/* Show remote screen share if available, otherwise show our screen share */}
              {remoteScreenTrack ? (
                <video
                  ref={el => {
                    if (el && remoteScreenTrack.publication.track) {
                      remoteScreenTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-contain"
                />
              ) : localScreenTrack ? (
                <video
                  ref={el => {
                    if (el && localScreenTrack.publication.track) {
                      localScreenTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain"
                />
              ) : null}
              <div className="absolute top-1 left-1 bg-purple-600/90 px-2 py-0.5 rounded text-[10px] text-white font-medium flex items-center gap-1">
                <Monitor className="w-3 h-3" />
                {remoteScreenTrack ? 'SCREEN (Client)' : 'SCREEN (You)'}
              </div>
            </div>
          )}
          
          {/* Camera Views */}
          <div className={`flex gap-1 ${screenShareEnabled || remoteScreenTrack ? 'h-20' : 'h-full'}`}>
            {/* Remote Video (Client) */}
            <div className="flex-1 relative rounded-lg overflow-hidden bg-gray-900">
              {remoteVideoTrack ? (
                <video
                  ref={el => {
                    if (el && remoteVideoTrack.publication.track) {
                      remoteVideoTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Users className="w-8 h-8 text-gray-600 mx-auto mb-1" />
                    <p className="text-gray-500 text-[10px]">
                      {isConnected ? 'Waiting...' : 'Connecting...'}
                    </p>
                  </div>
                </div>
              )}
              <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-[10px] text-blue-400 font-medium">
                CLIENT
              </div>
            </div>

            {/* Local Video (You) */}
            <div className={`${screenShareEnabled || remoteScreenTrack ? 'w-24' : 'w-28'} relative rounded-lg overflow-hidden border-2 border-green-500 bg-gray-900`}>
              {localVideoTrack && videoEnabled ? (
                <video
                  ref={el => {
                    if (el && localVideoTrack.publication.track) {
                      localVideoTrack.publication.track.attach(el);
                    }
                  }}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <VideoOff className="w-6 h-6 text-gray-500" />
                </div>
              )}
              <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-[10px] text-green-400 font-medium">
                YOU
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-gray-800 px-3 py-2 flex flex-col gap-2">
        {/* Big Screen Share Button - Only for Host */}
        {!screenShareEnabled && (
          <button
            onClick={toggleScreenShare}
            className="w-full py-2 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center justify-center gap-2 transition-colors"
          >
            <Monitor className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-sm">SHARE YOUR DAW WITH CLIENT</span>
          </button>
        )}
        
        {screenShareEnabled && (
          <button
            onClick={toggleScreenShare}
            className="w-full py-2 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center justify-center gap-2 animate-pulse"
          >
            <Monitor className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-sm">SHARING - Click to Stop</span>
          </button>
        )}
        
        {/* Regular Controls Row */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={toggleAudio}
            className={`p-2.5 rounded-full transition-colors ${audioEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'}`}
            title={audioEnabled ? 'Mute' : 'Unmute'}
          >
            {audioEnabled ? <Mic className="w-4 h-4 text-white" /> : <MicOff className="w-4 h-4 text-white" />}
          </button>

          <button
            onClick={toggleVideo}
            className={`p-2.5 rounded-full transition-colors ${videoEnabled ? 'bg-gray-700 hover:bg-gray-600' : 'bg-red-600 hover:bg-red-500'}`}
            title={videoEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {videoEnabled ? <Video className="w-4 h-4 text-white" /> : <VideoOff className="w-4 h-4 text-white" />}
          </button>

          <button
            onClick={onDisconnect}
            className="p-2.5 rounded-full bg-red-600 hover:bg-red-500 transition-colors"
            title="Leave call"
          >
            <PhoneOff className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      <RoomAudioRenderer />
    </>
  );
};

export default LiveKitVideoWidget;
